// src/pages/ApplicationChampionshipStageStandings/index.tsx
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, Skeleton, Switch, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { GetParamsObject } from '@/model/query-params';
import { Standing, StageType, ApplicationComponentId } from '@/model';

import { useChampionships } from '@/services/queries/championship';
import { useSeasons } from '@/services/queries/season';
import { useStages, useStageTeams } from '@/services/queries/stage';
import { useSeasonTeams } from '@/services/queries/season-team';
import {
  useDeleteStandingMutation,
  usePostStandingMutation,
  usePutStandingMutation,
  usePutStandingsOrderMutation,
  useStandings,
} from '@/services/queries/standing';

import { breadcrumb } from './breadcrumb';
import { StandingsFilters, StandingDraft, StandingEditableFields } from './types';
import { StandingsFilterBox } from './components/StandingsFilterBox';
import { StandingsHeaderActions } from './components/StandingsHeaderActions';
import { StandingsContextBanner } from './components/StandingsContextBanner';
import { StandingsEmptyState, StandingsEmptyVariant } from './components/StandingsEmptyState';
import { StandingsLegend } from './components/StandingsLegend';
import { StandingsTable } from './components/StandingsTable/StandingsTable';
import { EligibleTeam } from './components/StandingsTable/AddStandingRow';
import { buildSavePayload } from './utils/buildSavePayload';
import { calcGoalDiff, calcPlayed, calcWinRate } from './utils/calculateStandingDerived';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';

const PREFIX = 'pages.application_championship_stage_standings';
const STANDINGS_PAGE_SIZE = 200;

const EMPTY_FILTERS: StandingsFilters = {
  championship_uuid: '',
  season_uuid: '',
  stage_uuid: '',
};

export const ApplicationChampionshipStageStandings = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    ApplicationComponentId.TABLE_CLASSIFICATION,
  );
  const { mutate: enableAppAreaMutation, isPending: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);
  const [filters, setFilters] = useState<StandingsFilters>(EMPTY_FILTERS);
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [savingUuid, setSavingUuid] = useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [confirmDeleteUuid, setConfirmDeleteUuid] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isAddSaving, setIsAddSaving] = useState(false);
  /** Snapshot otimista de ordem após DnD; null = usa o cache do react-query. */
  const [localOrder, setLocalOrder] = useState<Standing[] | null>(null);
  const [isReorderInFlight, setIsReorderInFlight] = useState(false);

  // Queries de contexto (cache compartilhado com o FilterBox)
  const championshipsParams = useMemo(() => GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'name'), []);
  const { data: championshipsData } = useChampionships(championshipsParams);

  const seasonsParams = useMemo(
    () =>
      filters.championship_uuid
        ? GetParamsObject({ championship_uuid: filters.championship_uuid } as Record<string, string>, 1, 200, 'desc', 'year')
        : undefined,
    [filters.championship_uuid],
  );
  const { data: seasonsData } = useSeasons(seasonsParams);

  const stagesParams = useMemo(
    () =>
      filters.season_uuid
        ? GetParamsObject({ season_uuid: filters.season_uuid } as Record<string, string>, 1, 200, 'asc', 'stage_order')
        : undefined,
    [filters.season_uuid],
  );
  const { data: stagesData } = useStages(stagesParams);

  const selectedChampionship = useMemo(
    () => championshipsData?.data?.find(c => c.uuid === filters.championship_uuid),
    [championshipsData, filters.championship_uuid],
  );
  const selectedSeason = useMemo(
    () => seasonsData?.data?.find(s => s.uuid === filters.season_uuid),
    [seasonsData, filters.season_uuid],
  );
  const selectedStage = useMemo(
    () => stagesData?.data?.find(s => s.uuid === filters.stage_uuid),
    [stagesData, filters.stage_uuid],
  );

  const isLeague = selectedStage?.type === StageType.LEAGUE;
  const isGroup = selectedStage?.type === StageType.GROUP;
  const isKnockout = selectedStage?.type === StageType.KNOCKOUT;
  const canHaveStandings = isLeague || isGroup;

  // Standings da fase selecionada
  const standingsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, STANDINGS_PAGE_SIZE, 'asc', 'position'),
    [],
  );
  const standingsStageId = canHaveStandings && selectedStage ? selectedStage.uuid : '';
  const {
    data: standingsData,
    isLoading: isStandingsLoading,
    forceRefetch: forceRefetchStandings,
  } = useStandings(standingsStageId, standingsParams);

  // Times elegíveis para "Adicionar time"
  const seasonTeamsParams = useMemo(
    () =>
      filters.season_uuid
        ? GetParamsObject(
            { season_uuid: filters.season_uuid } as Record<string, string>,
            1,
            STANDINGS_PAGE_SIZE,
            'asc',
            'created_at',
          )
        : undefined,
    [filters.season_uuid],
  );
  const { data: seasonTeamsData } = useSeasonTeams(seasonTeamsParams, !!filters.season_uuid && isLeague);

  const stageTeamsParams = useMemo(() => GetParamsObject({} as Record<string, string>, 1, STANDINGS_PAGE_SIZE), []);
  const stageTeamsStageId = isGroup && selectedStage ? selectedStage.uuid : '';
  const { data: stageTeamsData } = useStageTeams(stageTeamsStageId, stageTeamsParams);

  // Mutations
  const queryClient = useQueryClient();
  const postStanding = usePostStandingMutation();
  const putStanding = usePutStandingMutation();
  const deleteStanding = useDeleteStandingMutation();
  const putOrder = usePutStandingsOrderMutation();

  // Standings exibidos: otimista (DnD) tem prioridade sobre o cache do server.
  const standings: Standing[] = useMemo(() => localOrder ?? standingsData?.data ?? [], [localOrder, standingsData]);

  const eligibleTeams: EligibleTeam[] = useMemo(() => {
    const alreadyInStandings = new Set(standings.map(s => s.team_uuid).filter(Boolean) as string[]);

    const source = isLeague
      ? (seasonTeamsData?.data ?? []).map(st => st.team).filter(Boolean)
      : isGroup
        ? (stageTeamsData?.data ?? []).map(st => st.team).filter(Boolean)
        : [];

    return source
      .filter((team): team is NonNullable<typeof team> => !!team && !alreadyInStandings.has(team.uuid))
      .map(team => ({
        uuid: team.uuid,
        name: team.name ?? '',
        acronym: team.acronym,
      }));
  }, [isLeague, isGroup, seasonTeamsData, stageTeamsData, standings]);

  const emptyVariant: StandingsEmptyVariant | null = useMemo(() => {
    if (!selectedStage) return 'select-stage';
    if (isKnockout) return 'knockout';
    if (!isStandingsLoading && standings.length === 0 && !isAdding) return 'no-standings';
    return null;
  }, [selectedStage, isKnockout, isStandingsLoading, standings.length, isAdding]);

  // === Handlers ===

  const handleFiltersChange = useCallback((next: StandingsFilters) => {
    setFilters(next);
    // Reset UI state ao trocar contexto
    setEditingUuid(null);
    setIsAdding(false);
    setLocalOrder(null);
  }, []);

  const handleClearFilters = useCallback(() => {
    handleFiltersChange(EMPTY_FILTERS);
  }, [handleFiltersChange]);

  const handleStartAdd = useCallback(() => {
    if (isAdding) return;
    setEditingUuid(null);
    setIsAdding(true);
  }, [isAdding]);

  const handleCancelAdd = useCallback(() => setIsAdding(false), []);

  const handleEnterEdit = useCallback(
    (uuid: string) => {
      if (isAdding) return;
      setEditingUuid(uuid);
    },
    [isAdding],
  );

  const handleCancelEdit = useCallback(() => setEditingUuid(null), []);

  const handleSaveEdit = useCallback(
    (uuid: string, changes: StandingEditableFields) => {
      setSavingUuid(uuid);
      putStanding.mutate(
        { id: uuid, payload: buildSavePayload(changes) },
        {
          onSuccess: () => {
            setSavingUuid(null);
            setEditingUuid(null);
            forceRefetchStandings();
            toast({ status: 'success', title: t(`${PREFIX}.toasts.update_success`) });
          },
          onError: (error: unknown) => {
            setSavingUuid(null);
            toast({
              status: 'error',
              title: (error as Error)?.message || t(`${PREFIX}.toasts.update_error`),
            });
          },
        },
      );
    },
    [putStanding, forceRefetchStandings, toast, t],
  );

  const handleDeleteRequest = useCallback((uuid: string) => {
    setConfirmDeleteUuid(uuid);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setConfirmDeleteUuid(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!confirmDeleteUuid) return;
    const uuid = confirmDeleteUuid;
    setConfirmDeleteUuid(null);
    setDeletingUuid(uuid);
    deleteStanding.mutate(uuid, {
      onSuccess: () => {
        setDeletingUuid(null);
        forceRefetchStandings();
        toast({ status: 'success', title: t(`${PREFIX}.toasts.delete_success`) });
      },
      onError: (error: unknown) => {
        setDeletingUuid(null);
        toast({
          status: 'error',
          title: (error as Error)?.message || t(`${PREFIX}.toasts.delete_error`),
        });
      },
    });
  }, [confirmDeleteUuid, deleteStanding, forceRefetchStandings, toast, t]);

  const handleSaveAdd = useCallback(
    (draft: StandingDraft) => {
      if (!selectedStage) return;
      const played = calcPlayed(draft.wins, draft.draws, draft.losses);
      const goalDiff = calcGoalDiff(draft.goals_for, draft.goals_against);
      const winRate = calcWinRate(draft.points, played);
      setIsAddSaving(true);
      postStanding.mutate(
        {
          id: selectedStage.uuid,
          payload: {
            team_uuid: draft.team_uuid,
            points: draft.points,
            played,
            wins: draft.wins,
            draws: draft.draws,
            losses: draft.losses,
            goals_for: draft.goals_for,
            goals_against: draft.goals_against,
            goal_diff: goalDiff,
            win_rate: winRate === null ? undefined : winRate,
          },
        },
        {
          onSuccess: () => {
            setIsAddSaving(false);
            setIsAdding(false);
            forceRefetchStandings();
            toast({ status: 'success', title: t(`${PREFIX}.toasts.add_success`) });
          },
          onError: (error: unknown) => {
            setIsAddSaving(false);
            toast({
              status: 'error',
              title: (error as Error)?.message || t(`${PREFIX}.toasts.add_error`),
            });
          },
        },
      );
    },
    [selectedStage, postStanding, forceRefetchStandings, toast, t],
  );

  const handleReorder = useCallback(
    (orderedUuids: string[]) => {
      if (!selectedStage) return;
      const snapshot = standings;
      const byUuid = new Map(standings.map(s => [s.uuid, s]));
      const reordered: Standing[] = orderedUuids
        .map(uuid => byUuid.get(uuid))
        .filter((s): s is Standing => !!s)
        .map((s, index) => ({ ...s, position: index + 1 }));
      setLocalOrder(reordered);
      setIsReorderInFlight(true);
      putOrder.mutate(
        {
          id: selectedStage.uuid,
          payload: { ordered_standing_uuids: orderedUuids, mode: 'full' },
        },
        {
          onSuccess: () => {
            // Patcha o cache do react-query com a nova ordem ANTES de soltar
            // o localOrder. Sem isso, há um flash visível pra ordem antiga
            // entre o setLocalOrder(null) e a chegada do refetch.
            queryClient.setQueriesData({ queryKey: ['GET_STANDINGS', selectedStage.uuid] }, (old: unknown) => {
              if (!old || typeof old !== 'object') return old;
              const previous = old as { data?: Standing[]; meta?: unknown };
              return { ...previous, data: reordered };
            });
            setIsReorderInFlight(false);
            setLocalOrder(null);
            // Refetch em background pra sincronizar updated_at do servidor.
            forceRefetchStandings();
          },
          onError: (error: unknown) => {
            setIsReorderInFlight(false);
            setLocalOrder(snapshot);
            toast({
              status: 'error',
              title: (error as Error)?.message || t(`${PREFIX}.toasts.reorder_error`),
            });
          },
        },
      );
    },
    [selectedStage, standings, putOrder, forceRefetchStandings, queryClient, toast, t],
  );

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingAppAreaState(event.target.checked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successMsg = pendingAppAreaState
      ? t(`${PREFIX}.enable_success`)
      : t(`${PREFIX}.disable_success`);
    const errorMsg = pendingAppAreaState
      ? t(`${PREFIX}.enable_error`)
      : t(`${PREFIX}.disable_error`);

    setIsAppAreaActive(pendingAppAreaState);
    enableAppAreaMutation([{ component_id: ApplicationComponentId.TABLE_CLASSIFICATION, active: pendingAppAreaState }], {
      onSuccess: () => toast({ status: 'success', title: successMsg }),
      onError: () => {
        toast({ status: 'error', title: errorMsg });
        setIsAppAreaActive(!pendingAppAreaState);
      },
    });
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;
    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  // === Render ===

  const addDisabled = !canHaveStandings || isAdding || eligibleTeams.length === 0;
  const addDisabledReason = !canHaveStandings
    ? isKnockout
      ? t(`${PREFIX}.header_actions.disabled_knockout`)
      : t(`${PREFIX}.header_actions.disabled_no_stage`)
    : isAdding
      ? t(`${PREFIX}.header_actions.disabled_in_progress`)
      : eligibleTeams.length === 0
        ? t(`${PREFIX}.header_actions.disabled_no_eligible`)
        : undefined;

  const itemToDelete = confirmDeleteUuid ? standings.find(s => s.uuid === confirmDeleteUuid) : undefined;

  return (
    <PageContent title={t(`${PREFIX}.page_title`)} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4} align={{ md: 'center' }}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t(`${PREFIX}.section_title`)}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive ? t(`${PREFIX}.switch_enabled`) : t(`${PREFIX}.switch_disabled`)}
                </Switch>
              </>
            )}
          </Flex>

          <StandingsHeaderActions onAdd={handleStartAdd} isDisabled={addDisabled} disabledReason={addDisabledReason} />
        </Flex>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <StandingsFilterBox filters={filters} onChange={handleFiltersChange} onClear={handleClearFilters} />
        </SectionBox>

        <Flex direction='column' gap={4} mt={4}>
          <StandingsContextBanner
            championshipName={selectedChampionship?.name}
            seasonYear={selectedSeason?.year}
            stage={selectedStage}
            teamCount={standings.length}
          />

          {emptyVariant ? (
            <StandingsEmptyState variant={emptyVariant} />
          ) : (
            <>
              <StandingsTable
                standings={standings}
                stage={selectedStage}
                editingUuid={editingUuid}
                savingUuid={savingUuid}
                deletingUuid={deletingUuid}
                isAdding={isAdding}
                isAddSaving={isAddSaving}
                eligibleTeams={eligibleTeams}
                onEnterEdit={handleEnterEdit}
                onCancelEdit={handleCancelEdit}
                onSaveEdit={handleSaveEdit}
                onDelete={handleDeleteRequest}
                onSaveAdd={handleSaveAdd}
                onCancelAdd={handleCancelAdd}
                onReorder={handleReorder}
                isReorderInFlight={isReorderInFlight}
              />
              <StandingsLegend stage={selectedStage} />
            </>
          )}
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={!!confirmDeleteUuid}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteStanding.isPending}
        title={t(`${PREFIX}.delete_modal.title`)}
        description={
          <Text>
            {t(`${PREFIX}.delete_modal.description_start`)}
            <Text as='span' fontWeight='bold'>
              {' '}
              "{itemToDelete?.team?.name ?? ''}"
            </Text>
            {t(`${PREFIX}.delete_modal.description_end`)}
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t(`${PREFIX}.toggle_modal_title`)}
        description={
          <Text>
            {pendingAppAreaState
              ? t(`${PREFIX}.enable_modal_description`)
              : t(`${PREFIX}.disable_modal_description`)}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};

export default ApplicationChampionshipStageStandings;
